import { useEffect, useState } from "react";

import Dropdown from "@/components/Dropdown";
import ErrorNotice from "@/components/ErrorNotice";
import { listGithubBranches, listGithubRepos } from "@/lib/api";
import { describeGithubError, type DescribedError } from "@/lib/github-errors";
import type { GithubRepo } from "@/lib/types";

export type GithubTarget = { repo: string; branch: string; subpath: string };

// Repo + branch + subpath picker shared by the NewSkill import tab and the Skill link panel.
// Loads the connected account's repos, then the chosen repo's branches (default-selecting the
// repo's default branch). `subpath` is the folder that directly contains SKILL.md.
export default function GithubPicker({
  token,
  value,
  onChange,
  disabled,
}: {
  token: string;
  value: GithubTarget;
  onChange: (v: GithubTarget) => void;
  disabled?: boolean;
}) {
  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);

  useEffect(() => {
    let active = true;
    setLoadingRepos(true);
    listGithubRepos(token)
      .then((r) => active && setRepos(r))
      .catch((e) => active && setError(describeGithubError(e, "repos")))
      .finally(() => active && setLoadingRepos(false));
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!value.repo) {
      setBranches([]);
      return;
    }
    let active = true;
    setLoadingBranches(true);
    listGithubBranches(value.repo, token)
      .then((b) => active && setBranches(b))
      .catch(() => active && setBranches([]))
      .finally(() => active && setLoadingBranches(false));
    return () => {
      active = false;
    };
  }, [value.repo, token]);

  function onRepo(full: string) {
    const repo = repos.find((r) => r.full_name === full);
    onChange({ repo: full, branch: repo?.default_branch ?? "main", subpath: value.subpath });
  }

  const fieldCls =
    "w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-text outline-none transition focus:border-primary/60 focus:bg-white/[0.05] disabled:cursor-not-allowed disabled:opacity-50";
  const labelCls = "mb-1.5 block text-xs font-medium text-text/55";

  return (
    <div className="space-y-4">
      <ErrorNotice error={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={labelCls}>Repository</span>
          <Dropdown
            value={value.repo}
            onChange={onRepo}
            ariaLabel="Repository"
            disabled={disabled}
            loading={loadingRepos}
            placeholder="Select a repository"
            options={repos.map((r) => ({
              value: r.full_name,
              label: `${r.full_name}${r.private ? " (private)" : ""}`,
            }))}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Branch</span>
          <Dropdown
            value={value.branch}
            onChange={(b) => onChange({ ...value, branch: b })}
            ariaLabel="Branch"
            disabled={disabled || !value.repo}
            loading={loadingBranches}
            placeholder={value.branch || "Select a branch"}
            options={branches.map((b) => ({ value: b, label: b }))}
          />
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>
          Folder <span className="text-text/35">· optional</span>
        </span>
        <input
          value={value.subpath}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, subpath: e.target.value })}
          placeholder="Leave blank for repo root"
          className={fieldCls}
        />
        <span className="mt-1.5 block text-xs text-text/40">The folder that contains SKILL.md.</span>
      </label>
    </div>
  );
}
